import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { 
  Grid, 
  Card, 
  CardContent, 
  Typography, 
  Box, 
  Button, 
  Tabs, 
  Tab, 
  CircularProgress, 
  Paper,
  Stack,
  IconButton,
  Dialog,
  DialogContent
} from '@mui/material';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import { fetchCctvStreams } from '../features/cctv/cctvSlice';

// 模擬 CCTV 視頻流
const VideoStream = ({ stream, isFullscreen, handleFullscreen }) => {
  const [isPlaying, setIsPlaying] = useState(true);

  const togglePlayPause = (e) => {
    e.stopPropagation();
    setIsPlaying(!isPlaying);
  };

  // 在實際應用中，這將是真實的視頻流
  return (
    <Card 
      sx={{ 
        position: 'relative', 
        height: isFullscreen ? '90vh' : '100%', 
        width: isFullscreen ? '100%' : '100%',
        cursor: 'pointer'
      }}
      onClick={() => handleFullscreen(stream.id)}
    >
      <Box 
        sx={{ 
          backgroundColor: '#000', 
          height: isFullscreen ? 'calc(90vh - 60px)' : '200px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        {/* 在這裡，你會整合實際的視頻串流 */}
        <Typography variant="body2" color="white">
          {isPlaying ? '📹 實時串流中...' : '⏸️ 已暫停'}
        </Typography>
      </Box>
      <Box 
        sx={{ 
          position: 'absolute', 
          bottom: 0, 
          width: '100%', 
          padding: 1,
          backgroundColor: 'rgba(0, 0, 0, 0.6)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}
      >
        <Typography variant="body2" color="white" sx={{ flexGrow: 1 }}>
          {stream.name}
        </Typography>
        <Box>
          <IconButton size="small" color="inherit" onClick={togglePlayPause}>
            {isPlaying ? <PauseIcon fontSize="small" /> : <PlayArrowIcon fontSize="small" />}
          </IconButton>
          <IconButton size="small" color="inherit" onClick={(e) => {
            e.stopPropagation();
            handleFullscreen(stream.id);
          }}>
            {isFullscreen ? <FullscreenExitIcon fontSize="small" /> : <FullscreenIcon fontSize="small" />}
          </IconButton>
        </Box>
      </Box>
    </Card>
  );
};

// 根據用戶角色過濾可用的 CCTV 流
const filterStreamsByRole = (streams, userRole, userUnit) => {
  if (!streams) return [];
  
  switch (userRole) {
    case 'resident':
    case 'tenant':
      // 住戶和租戶只能看到大廈入口、地下大堂和自己樓層的攝影機
      return streams.filter(stream => 
        stream.isMainEntrance || 
        stream.isGroundLobby || 
        (stream.floor === userUnit?.floor) ||
        (stream.unitId === userUnit?.id)
      );
    case 'security':
    case 'manager':
      // 保安和管理員可以看到所有攝影機
      return streams;
    default:
      return [];
  }
};

const CCTVViewer = () => {
  const [selectedTab, setSelectedTab] = useState(0);
  const [fullscreenStream, setFullscreenStream] = useState(null);
  const dispatch = useDispatch();
  const { streams, loading, error } = useSelector(state => state.cctv);
  const { userRole, userUnit } = useSelector(state => state.auth);
  
  useEffect(() => {
    dispatch(fetchCctvStreams());
  }, [dispatch]);
  
  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
  };
  
  const toggleFullscreen = (streamId) => {
    if (fullscreenStream === streamId) {
      setFullscreenStream(null);
    } else {
      setFullscreenStream(streamId);
    }
  };
  
  const filteredStreams = filterStreamsByRole(streams, userRole, userUnit);
  
  // 按位置分組流
  const entranceStreams = filteredStreams.filter(s => s.isMainEntrance);
  const lobbyStreams = filteredStreams.filter(s => s.isGroundLobby);
  const floorStreams = filteredStreams.filter(s => s.floor === userUnit?.floor && !s.unitId);
  const unitStreams = filteredStreams.filter(s => s.unitId === userUnit?.id);
  const otherStreams = filteredStreams.filter(s => 
    !s.isMainEntrance && !s.isGroundLobby && s.floor !== userUnit?.floor && s.unitId !== userUnit?.id
  );
  
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }
  
  if (error) {
    return (
      <Box sx={{ mt: 2 }}>
        <Paper sx={{ p: 2, bgcolor: '#fff9c4' }}>
          <Typography color="error">
            無法載入攝影機: {error}
          </Typography>
        </Paper>
      </Box>
    );
  }
  
  return (
    <>
      <Box sx={{ mb: 2 }}>
        <Typography variant="h5" gutterBottom>CCTV 監控</Typography>
        
        <Paper sx={{ mb: 2 }}>
          <Tabs 
            value={selectedTab} 
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab label="所有攝影機" />
            <Tab label="主要入口" disabled={entranceStreams.length === 0} />
            <Tab label="大堂" disabled={lobbyStreams.length === 0} />
            <Tab label="樓層" disabled={floorStreams.length === 0} />
            <Tab label="單位" disabled={unitStreams.length === 0} />
            {userRole === 'security' || userRole === 'manager' ? 
              <Tab label="其他區域" disabled={otherStreams.length === 0} /> 
              : null}
          </Tabs>
        </Paper>
        
        <Grid container spacing={2}>
          {selectedTab === 0 && filteredStreams.map(stream => (
            <Grid item xs={12} sm={6} md={4} key={stream.id}>
              <VideoStream 
                stream={stream} 
                isFullscreen={fullscreenStream === stream.id}
                handleFullscreen={toggleFullscreen}
              />
            </Grid>
          ))}
          
          {selectedTab === 1 && entranceStreams.map(stream => (
            <Grid item xs={12} sm={6} md={4} key={stream.id}>
              <VideoStream 
                stream={stream} 
                isFullscreen={fullscreenStream === stream.id}
                handleFullscreen={toggleFullscreen}
              />
            </Grid>
          ))}
          
          {selectedTab === 2 && lobbyStreams.map(stream => (
            <Grid item xs={12} sm={6} md={4} key={stream.id}>
              <VideoStream 
                stream={stream} 
                isFullscreen={fullscreenStream === stream.id}
                handleFullscreen={toggleFullscreen}
              />
            </Grid>
          ))}
          
          {selectedTab === 3 && floorStreams.map(stream => (
            <Grid item xs={12} sm={6} md={4} key={stream.id}>
              <VideoStream 
                stream={stream} 
                isFullscreen={fullscreenStream === stream.id}
                handleFullscreen={toggleFullscreen}
              />
            </Grid>
          ))}
          
          {selectedTab === 4 && unitStreams.map(stream => (
            <Grid item xs={12} sm={6} md={4} key={stream.id}>
              <VideoStream 
                stream={stream} 
                isFullscreen={fullscreenStream === stream.id}
                handleFullscreen={toggleFullscreen}
              />
            </Grid>
          ))}
          
          {selectedTab === 5 && otherStreams.map(stream => (
            <Grid item xs={12} sm={6} md={4} key={stream.id}>
              <VideoStream 
                stream={stream} 
                isFullscreen={fullscreenStream === stream.id}
                handleFullscreen={toggleFullscreen}
              />
            </Grid>
          ))}
        </Grid>
      </Box>
      
      <Dialog
        open={Boolean(fullscreenStream)}
        onClose={() => setFullscreenStream(null)}
        maxWidth="lg"
        fullWidth
      >
        <DialogContent sx={{ p: 1 }}>
          {fullscreenStream && (
            <VideoStream 
              stream={filteredStreams.find(s => s.id === fullscreenStream)} 
              isFullscreen={true}
              handleFullscreen={toggleFullscreen}
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default CCTVViewer; 